import React, { useEffect, useState } from "react";

const API = "https://smart-bin-backend-zygj.onrender.com";
const token = () => localStorage.getItem("token");

function OperatorDashboard() {
  const [pickups, setPickups] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locError, setLocError] = useState("");

  // Machine registration / location-proposal state
  const [regMachineId, setRegMachineId] = useState("");
  const [regLocation, setRegLocation] = useState("");
  const [regMsg, setRegMsg] = useState("");
  const [regSubmitting, setRegSubmitting] = useState(false);

  const email = localStorage.getItem("email");

  const fetchPickups = async () => {
    try {
      const res = await fetch(`${API}/api/pickups`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      const data = await res.json();
      setPickups(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  // Operators can see machines too (for registering locations) --
  // uses the same GET /api/machines route the admin dashboard uses.
  const fetchMachines = async () => {
    try {
      const res = await fetch(`${API}/api/machines`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMachines(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAll = async () => {
    await Promise.all([fetchPickups(), fetchMachines()]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleMarkCollected = async (pickupId) => {
    try {
      await fetch(`${API}/api/pickups/${pickupId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`
        },
        body: JSON.stringify({ status: "collected" })
      });
      fetchPickups();
    } catch (err) {
      console.error(err);
    }
  };

  const handleAssign = async (pickupId) => {
    try {
      const userId = localStorage.getItem("userId");
      await fetch(`${API}/api/pickups/${pickupId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`
        },
        body: JSON.stringify({ status: "assigned", operatorId: userId })
      });
      fetchPickups();
    } catch (err) {
      console.error(err);
    }
  };

  // Submits a proposed location for a machine ID -- goes to
  // POST /api/machines/:machineId/propose-location. Does NOT set the
  // live location; an admin must approve it first. Any registered
  // machine (seen via heartbeat) can have a location proposed for it,
  // even before this operator "finds" it in the list -- so the machine
  // ID can be typed in directly (e.g. read off the kiosk screen).
  const handleProposeLocation = async () => {
    setRegMsg("");
    if (!regMachineId.trim()) {
      setRegMsg("❌ Machine ID is required — read it off the kiosk screen.");
      return;
    }
    if (!regLocation.trim()) {
      setRegMsg("❌ Location is required.");
      return;
    }
    setRegSubmitting(true);
    try {
      const res = await fetch(
        `${API}/api/machines/${encodeURIComponent(regMachineId.trim())}/propose-location`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token()}`
          },
          body: JSON.stringify({ location: regLocation.trim() })
        }
      );
      const data = await res.json();
      if (res.ok) {
        setRegMsg("✅ Location proposed. Waiting for admin approval.");
        setRegMachineId("");
        setRegLocation("");
        fetchMachines();
      } else {
        setRegMsg("❌ " + (data.message || "Failed to propose location."));
      }
    } catch (err) {
      setRegMsg("❌ Network error.");
    } finally {
      setRegSubmitting(false);
    }
  };

  // Gets the operator's current location, then opens Google Maps
  // Directions from that location to the pickup's text address.
  // Nothing is stored -- this is a one-time live lookup per click.
  const handleGetDirections = (address) => {
    setLocError("");

    if (!address || !address.trim()) {
      setLocError("This pickup has no address on file.");
      return;
    }

    if (!navigator.geolocation) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`;
      window.open(url, "_blank");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const url =
          `https://www.google.com/maps/dir/?api=1` +
          `&origin=${latitude},${longitude}` +
          `&destination=${encodeURIComponent(address)}` +
          `&travelmode=driving`;
        window.open(url, "_blank");
      },
      (err) => {
        console.error("Geolocation error:", err);
        setLocError("Could not get your location. Opening directions without a start point.");
        const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`;
        window.open(url, "_blank");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleLogout = () => { localStorage.clear(); window.location.href = "/"; };

  const pending   = pickups.filter(p => p.status === "pending");
  const assigned  = pickups.filter(p => p.status === "assigned");
  const collected = pickups.filter(p => p.status === "collected");

  if (loading) return <div style={styles.loading}>Loading...</div>;

  return (
    <div style={styles.bg}>
      <div style={styles.container}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Operator Panel</h1>
            <p style={styles.sub}>{email}</p>
          </div>
          <button style={styles.logoutBtn} onClick={handleLogout}>Logout</button>
        </div>

        {locError && <p style={styles.locError}>{locError}</p>}

        {/* Summary */}
        <div style={styles.summaryRow}>
          <SummaryCard label="Pending"   value={pending.length}   color="#C49A6C" />
          <SummaryCard label="Assigned"  value={assigned.length}  color="#2E4A3A" />
          <SummaryCard label="Collected" value={collected.length} color="#1a6b3c" />
        </div>

        {/* Register machine location */}
        <Section title="📍 Register Machine Location">
          <p style={styles.regHelp}>
            At the machine, note the ID shown on its screen (e.g. RB-X7RGE4), then enter
            it here with the site's location. An admin will review and approve it.
          </p>
          <div style={styles.regRow}>
            <input
              style={styles.regInput}
              placeholder="Machine ID (e.g. RB-X7RGE4)"
              value={regMachineId}
              onChange={e => setRegMachineId(e.target.value)}
            />
            <input
              style={styles.regInput}
              placeholder="Location (e.g. BVM CoEDM, Vallabh Vidyanagar)"
              value={regLocation}
              onChange={e => setRegLocation(e.target.value)}
            />
            <button
              style={styles.regBtn}
              onClick={handleProposeLocation}
              disabled={regSubmitting}
            >
              {regSubmitting ? "Submitting..." : "Submit for Approval"}
            </button>
          </div>
          {regMsg && (
            <p style={{ ...styles.regMsg, color: regMsg.startsWith("✅") ? "#1a6b3c" : "#B43232" }}>
              {regMsg}
            </p>
          )}

          {machines.length > 0 && (
            <div style={styles.machineListWrap}>
              <p style={styles.machineListLabel}>Known machines:</p>
              {machines.map(m => (
                <div key={m.machineId} style={styles.machineListRow}>
                  <span style={styles.machineListId}>{m.machineId}</span>
                  {m.locationStatus === "approved" && (
                    <span style={styles.machineListLoc}>📍 {m.location}</span>
                  )}
                  {m.locationStatus === "pending_approval" && (
                    <span style={styles.machineListPending}>
                      ⏳ Proposed: {m.proposedLocation} — awaiting approval
                    </span>
                  )}
                  {m.locationStatus === "unassigned" && (
                    <span style={styles.machineListUnassigned}>No location yet</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Pending Pickups */}
        <Section title="🔴 Pending Pickups">
          {pending.length === 0
            ? <p style={styles.empty}>No pending pickups.</p>
            : pending.map(p => (
              <PickupCard
                key={p._id} pickup={p}
                onDirections={handleGetDirections}
                action={{ label: "Assign to me", fn: () => handleAssign(p.pickupId), color: "#2E4A3A" }}
              />
            ))
          }
        </Section>

        {/* Assigned to me */}
        <Section title="🟡 Assigned to Me">
          {assigned.length === 0
            ? <p style={styles.empty}>No pickups assigned yet.</p>
            : assigned.map(p => (
              <PickupCard
                key={p._id} pickup={p}
                onDirections={handleGetDirections}
                action={{ label: "Mark Collected", fn: () => handleMarkCollected(p.pickupId), color: "#1a6b3c" }}
              />
            ))
          }
        </Section>

        {/* Collected */}
        <Section title="✅ Collected Today">
          {collected.length === 0
            ? <p style={styles.empty}>None collected yet.</p>
            : collected.slice(0, 5).map(p => (
              <PickupCard key={p._id} pickup={p} onDirections={handleGetDirections} />
            ))
          }
        </Section>

      </div>
    </div>
  );
}

function PickupCard({ pickup, action, onDirections }) {
  const hasAddress = pickup.address && pickup.address.trim().length > 0;

  return (
    <div style={styles.pickupCard}>
      <div style={styles.pickupTop}>
        <span style={styles.pickupId}>{pickup.pickupId}</span>
        <StatusPill status={pickup.status} />
      </div>
      <p style={styles.pickupMachine}>Machine: {pickup.machineId}</p>
      <p style={styles.pickupReason}>Reason: {pickup.reason}</p>

      {hasAddress && (
        <p style={styles.pickupAddress}>📍 {pickup.address}</p>
      )}

      {pickup.createdAt &&
        <p style={styles.pickupTime}>
          {new Date(pickup.createdAt).toLocaleString("en-IN")}
        </p>
      }

      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {hasAddress && onDirections && (
          <button
            style={{ ...styles.actionBtn, background: "#3367d6" }}
            onClick={() => onDirections(pickup.address)}
          >
            🧭 Get Directions
          </button>
        )}
        {action && (
          <button
            style={{ ...styles.actionBtn, background: action.color }}
            onClick={action.fn}
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{ ...styles.summaryCard, borderTop: `4px solid ${color}` }}>
      <p style={styles.summaryLabel}>{label}</p>
      <p style={{ ...styles.summaryValue, color }}>{value}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={styles.section}>
      <h2 style={styles.sectionTitle}>{title}</h2>
      {children}
    </div>
  );
}

function StatusPill({ status }) {
  const colors = { pending: "#C49A6C", assigned: "#2E4A3A", collected: "#1a6b3c" };
  return (
    <span style={{ ...styles.pill, background: colors[status] || "#888" }}>
      {status}
    </span>
  );
}

const styles = {
  bg:            { minHeight: "100vh", background: "#f0f4f0", padding: "20px" },
  container:     { maxWidth: "700px", margin: "0 auto" },
  header:        { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" },
  title:         { margin: 0, fontSize: "24px", color: "#1E3B2C", fontWeight: 700 },
  sub:           { margin: "4px 0 0", color: "#666", fontSize: "13px" },
  logoutBtn:     { padding: "8px 16px", background: "#1E3B2C", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer" },
  locError:      { background: "#fdecea", color: "#b43232", padding: "10px 14px", borderRadius: "8px", fontSize: "13px", marginBottom: "16px" },
  summaryRow:    { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "12px", marginBottom: "20px" },
  summaryCard:   { background: "#fff", borderRadius: "10px", padding: "14px", textAlign: "center", boxShadow: "0 2px 6px rgba(0,0,0,0.07)" },
  summaryLabel:  { margin: "0 0 4px", color: "#666", fontSize: "12px", textTransform: "uppercase" },
  summaryValue:  { margin: 0, fontSize: "26px", fontWeight: 700 },
  section:       { background: "#fff", borderRadius: "12px", padding: "18px", marginBottom: "16px", boxShadow: "0 2px 6px rgba(0,0,0,0.07)" },
  sectionTitle:  { margin: "0 0 14px", fontSize: "16px", color: "#1E3B2C", fontWeight: 600 },
  empty:         { color: "#aaa", fontSize: "14px" },
  regHelp:       { margin: "0 0 12px", fontSize: "13px", color: "#888" },
  regRow:        { display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "8px" },
  regInput:      { flex: "1 1 200px", padding: "10px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "13px", outline: "none" },
  regBtn:        { padding: "10px 18px", background: "#2E4A3A", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap" },
  regMsg:        { margin: "8px 0 0", fontSize: "13px" },
  machineListWrap:      { marginTop: "16px", paddingTop: "12px", borderTop: "1px solid #f0f0f0" },
  machineListLabel:     { margin: "0 0 8px", fontSize: "12px", color: "#888", textTransform: "uppercase" },
  machineListRow:       { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", fontSize: "13px" },
  machineListId:        { fontWeight: 600, color: "#1E3B2C" },
  machineListLoc:        { color: "#1a6b3c" },
  machineListPending:    { color: "#C49A6C" },
  machineListUnassigned: { color: "#aaa" },
  pickupCard:    { border: "1px solid #eee", borderRadius: "10px", padding: "14px", marginBottom: "10px" },
  pickupTop:     { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" },
  pickupId:      { fontWeight: 700, color: "#1E3B2C", fontSize: "15px" },
  pickupMachine: { margin: "2px 0", color: "#555", fontSize: "13px" },
  pickupReason:  { margin: "2px 0", color: "#888", fontSize: "12px" },
  pickupAddress: { margin: "4px 0", color: "#1E3B2C", fontSize: "13px", fontWeight: 500 },
  pickupTime:    { margin: "4px 0 8px", color: "#aaa", fontSize: "11px" },
  actionBtn:     { padding: "8px 16px", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600 },
  pill:          { color: "#fff", fontSize: "11px", padding: "3px 10px", borderRadius: "20px", fontWeight: 600 },
  loading:       { display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", color: "#1E3B2C" },
};

export default OperatorDashboard;